<template>
  <div>
    <load-profile />
    <b-card>
      <!-- Basic Information -->
      <h4 class="section-title">
        {{ $t('service.basic_info') }}
      </h4>
      <b-row>
        <b-col
          md="6"
          class="mb-2"
        >
          <div class="detail-group">
            <label>{{ $t('field.firstName') }}</label>
            <p class="mb-0">
              {{ data.firstName || 'N/A' }}
            </p>
          </div>
        </b-col>
        <b-col
          md="6"
          class="mb-2"
        >
          <div class="detail-group">
            <label>{{ $t('field.lastName') }}</label>
            <p class="mb-0">
              {{ data.lastName || 'N/A' }}
            </p>
          </div>
        </b-col>
        <b-col
          v-if="!isTelegramUser"
          md="6"
          class="mb-2"
        >
          <div class="detail-group">
            <label>{{ $t('field.email') }}</label>
            <p class="mb-0">
              {{ data.email || 'N/A' }}
            </p>
          </div>
        </b-col>
        <b-col
          md="6"
          class="mb-2"
        >
          <div class="detail-group">
            <label>{{ $t('field.phone') }}</label>
            <p class="mb-0">
              {{ data.phone || 'N/A' }}
            </p>
          </div>
        </b-col>
        <b-col
          v-if="!isTelegramUser"
          md="6"
          class="mb-2"
        >
          <div class="detail-group">
            <label>{{ $t('general.chat_cap') }}</label>
            <p class="mb-0">
              {{ data.serviceCap || 'N/A' }}
            </p>
          </div>
        </b-col>
        <b-col
          v-if="!isTelegramUser"
          md="6"
          class="mb-2"
        >
          <div class="detail-group">
            <label>{{ $t('field.serviceCap') }}</label>
            <p class="mb-0">
              {{ data.totalServiceCap || 'N/A' }}
            </p>
          </div>
        </b-col>
        <b-col
          md="6"
          class="mb-2"
        >
          <div class="detail-group">
            <label>{{ $t('field.role') }}</label>
            <p class="mb-0">
              {{ getRoleName(data.role) }}
            </p>
          </div>
        </b-col>
      </b-row>

      <!-- Projects Section -->
      <template v-if="!$route.query.isBot">
        <h4 class="section-title mt-4">
          {{ $t('breadcrumb.project') }}
        </h4>
        <div class="detail-group mb-3">
          <div
            v-if="data.projects && data.projects.length"
            class="d-flex flex-wrap"
          >
            <b-badge
              v-for="project in data.projects"
              :key="project.id"
              variant="primary"
              class="mb-1 badge-item"
              style="margin-right: 0.5rem;"
            >
              {{ project.id === 'ALL_PROJECT' ? $t('chat.all') : getProjectName(project) }}
            </b-badge>
          </div>
          <p
            v-else
            class="no-data-text"
          >
            {{ $t('general.noData') }}
          </p>
        </div>

        <!-- Department and Team Section -->
        <h4 class="section-title mt-4">
          {{ $t('general.department_and_team') }}
        </h4>
        <div class="detail-cards">
          <div v-if="data.departmentAndTeamInfo && data.departmentAndTeamInfo.length">
            <b-card
              v-for="(param, index) in data.departmentAndTeamInfo"
              :key="index"
              class="detail-card mb-2"
              no-body
            >
              <b-card-body>
                <div class="detail-group mb-2">
                  <label>{{ $t('breadcrumb.department') }}</label>
                  <p class="mb-0">
                    {{ getDepartmentName(param.departmentName) }}
                  </p>
                </div>
                <div class="detail-group">
                  <label>{{ $t('breadcrumb.team') }}</label>
                  <div
                    v-if="param.team && param.team.length"
                    class="d-flex flex-wrap"
                  >
                    <b-badge
                      v-for="team in param.team"
                      :key="team.id"
                      variant="info"
                      class="mb-1 badge-item"
                      style="margin-right: 0.5rem;"
                    >
                      {{ getTeamName(team) || 'N/A' }}
                    </b-badge>
                  </div>
                  <p
                    v-else
                    class="no-data-text mb-0"
                  >
                    {{ $t('general.noData') }}
                  </p>
                </div>
              </b-card-body>
            </b-card>
          </div>
          <p
            v-else
            class="no-data-text"
          >
            {{ $t('general.noData') }}
          </p>
        </div>

        <!-- Service Categories and Types Section -->
        <h4 class="section-title mt-4">
          {{ $t('general.service_category_and_type') }}
        </h4>
        <div class="detail-cards">
          <div v-if="data.serviceCateAndType && data.serviceCateAndType.length">
            <b-card
              class="detail-card mb-2"
              no-body
            >
              <b-card-body>
                <div class="detail-group mb-2">
                  <label>{{ $t('breadcrumb.serviceCategory') }}</label>
                  <div class="d-flex flex-wrap">
                    <b-badge
                      v-for="category in data.serviceCateAndType"
                      :key="category.serviceCategory.code"
                      variant="primary"
                      class="mb-1 badge-item"
                      style="margin-right: 0.5rem;"
                    >
                      {{ category.serviceCategory.code === 'ALL_CATEGORY'
                        ? $t('chat.all')
                        : getNameByLang(category.serviceCategory) || 'N/A' }}
                    </b-badge>
                  </div>
                </div>
                <div class="detail-group">
                  <label>{{ $t('breadcrumb.serviceType') }}</label>
                  <div class="d-flex flex-wrap">
                    <b-badge
                      v-for="type in getAllServiceTypes"
                      :key="type.code"
                      variant="primary"
                      class="mb-1 badge-item"
                      style="margin-right: 0.5rem;"
                    >
                      {{ type.code === 'ALL_TYPE'
                        ? $t('chat.all')
                        : getNameByLang(type) || 'N/A' }}
                    </b-badge>
                  </div>
                </div>
                <div class="detail-group">
                  <label>{{ $t('breadcrumb.serviceSubType') }}</label>
                  <div class="d-flex flex-wrap">
                    <b-badge
                      v-for="subType in getAllServiceSubTypes"
                      :key="subType.code"
                      variant="primary"
                      class="mb-1 badge-item"
                      style="margin-right: 0.5rem;"
                    >
                      {{ subType.code === 'ALL_SUBTYPE'
                        ? $t('chat.all')
                        : getNameByLang(subType) || 'N/A' }}
                    </b-badge>
                  </div>
                </div>
              </b-card-body>
            </b-card>
          </div>
          <p
            v-else
            class="no-data-text"
          >
            {{ $t('general.noData') }}
          </p>
        </div>
      </template>

      <!-- Action Buttons -->
      <div class="text-right mt-4">
        <b-button
          variant="outline-secondary"
          class="px-3"
          @click="back"
        >
          {{ $t('button.back') }}
        </b-button>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BBadge,
  BCardBody,
} from 'bootstrap-vue'
import Repository from '@/repositories/RepositoryFactory'

const OperatorRepository = Repository.get('operator')

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BBadge,
    BCardBody,
  },
  data() {
    return {
      data: {
        firstName: null,
        lastName: null,
        email: null,
        phone: null,
        roleId: null,
        isEnable: 1,
        serviceCap: '5',
        totalServiceCap: '5',
        projects: [],
        departmentAndTeam: [],
        serviceCateAndType: [],
      },
    }
  },
  computed: {
    locale() {
      return this.$i18n.locale.split('-')[0]
    },
    isTelegramUser() {
      return !this.data.email
    },
    getAllServiceSubTypes() {
      const allTypes = this.data.serviceCateAndType.reduce((types, category) => {
        if (category.serviceTypes && category.serviceTypes.length) {
          types.push(...category.serviceTypes)
        }
        return types
      }, [])

      // sub type in under service type
      const allSubTypes = allTypes.reduce((subTypes, type) => {
        if (type.serviceSubTypes && type.serviceSubTypes.length) {
          subTypes.push(...type.serviceSubTypes)
        }
        return subTypes
      }, [])

      // remove duplicate sub types
      return allSubTypes.filter((subType, index, self) => index === self.findIndex(t => t.code === subType.code))
    },
    getAllServiceTypes() {
      // Get all service types and filter unique ones by code
      const allTypes = this.data.serviceCateAndType.reduce((types, category) => {
        if (category.serviceTypes && category.serviceTypes.length) {
          types.push(...category.serviceTypes)
        }
        return types
      }, [])

      // Use Map to keep only unique types based on code
      const uniqueTypes = new Map()
      allTypes.forEach(type => {
        if (!uniqueTypes.has(type.code)) {
          uniqueTypes.set(type.code, type)
        }
      })

      return Array.from(uniqueTypes.values())
    },
  },
  mounted() {
    this.show()
  },
  methods: {
    getProjectName(project) {
      const locale = this.$i18n.locale.split('-')[0]
      const name = project?.name
      if (!name) return 'N/A'
      if (typeof name === 'string') return name
      return name?.[locale] || name?.zh || name?.en || name?.km || name?.vi || 'N/A'
    },
    getRoleName(role) {
      const roleName = role?.name
      if (!roleName) return 'N/A'

      if (typeof roleName === 'string') {
        return roleName
      }

      return roleName?.[this.locale] || roleName?.zh || roleName?.en || roleName?.km || roleName?.vi || 'N/A'
    },
    getDepartmentName(departmentName) {
      if (!departmentName) return 'N/A'

      if (typeof departmentName === 'string') {
        return departmentName
      }

      return departmentName?.[this.locale] || departmentName?.zh || departmentName?.en || departmentName?.km || departmentName?.vi || 'N/A'
    },
    show() {
      OperatorRepository.show(this.$route.params.id)
        .then(response => {
          const data = response?.data?.data
          this.data = {
            ...data,
            serviceCap: data.serviceCap?.toString() || '5',
            totalServiceCap: data.totalServiceCap?.toString() || '5',
            projects: data.projects?.map(project => ({
              id: project.projectId,
              name: project.translationsObj || project.name,
            })) || [],
            departmentAndTeam: data.departmentAndTeamInfo?.map(item => ({
              departmentId: item.departmentId,
              departmentName: item.departmentName,
              teams: item.team || [],
            })) || [],
            serviceCateAndType: data.serviceCateAndType?.map(item => ({
              name: item.name,
              serviceCategory: {
                code: item.serviceCategoryCode,
                name: item.name,
              },
              serviceTypes: item.serviceTypes || [],
            })) || [],
          }
        })
        .catch(error => {
          console.error('Error fetching operator details:', error)
        })
    },
    back() {
      this.$router.push({ name: 'list-operator' })
    },
    getNameByLang(data) {
      if (!data?.name) return ''

      return data.name?.[this.locale] || data.name?.zh || data.name?.en || data.name?.km || data.name?.vi || ''
    },
    getTeamName(team) {
      if (!team?.teamName) return ''

      return team.teamName?.[this.locale] || team.teamName?.zh || team.teamName?.en || team.teamName?.km || team.teamName?.vi || ''
    },
  },
}
</script>

<style lang="scss" scoped>
.detail-group {
  label {
    font-weight: 600;
    color: #6e6b7b;
    font-size: 0.857rem;
    margin-bottom: 0.5rem;
    text-transform: capitalize;
  }
  p {
    color: #5e5873;
    font-size: 1rem;
  }
}

.detail-cards {
  .detail-card {
    border: 1px solid rgba(47, 47, 47, 0.1);
    border-radius: 0.428rem;
    transition: all 0.3s ease;
    margin-bottom: 1rem;

    &:hover {
      box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
    }

    .card-body {
      padding: 1.5rem;
    }
  }
}

.section-title {
  color: #5e5873;
  font-size: 1.286rem;
  font-weight: 500;
  margin-bottom: 1.5rem;
  position: relative;
  padding-bottom: 0.5rem;

}

.badge-item {
  padding: 0.5rem 0.9rem;
  font-size: 0.857rem;
  font-weight: 500;
  border-radius: 0.358rem;
}

.no-data-text {
  color: #b9b9c3;
  font-style: italic;
  margin: 0;
  font-size: 0.857rem;
}

.compact-container {
  width: 100%;
  border: 1px solid rgba(47, 47, 47, 0.1);
  padding: 1rem;
  margin: 1rem;
  border-radius: 5px;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .compact-card {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    padding: 1rem;
    border: 1px solid rgba(47, 47, 47, 0.1);
    border-radius: 5px;
    background-color: #fff;

    .key, .value {
      flex: 1;
    }
  }
}
</style>
